/** In-editor test render: renders THIS notification against a chosen context.
 *  With a `deliveryMethodId` it previews that method INCLUDING the unsaved
 *  draft edits (field_overrides + notification_overrides — never saved);
 *  without one it renders all enabled channels from the SAVED state.
 */
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import type { NotificationDetail } from '@/api/notifications';
import { DELIVERY_METHOD_LABELS } from '@/api/notifications';
import { listTestRenderEvents, testRender, type TestRenderResult } from '@/api/notificationTestRender';
import { AppDialog } from '@/components/AppDialog';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { eventActions, notificationActions } from '../notificationLinks';
import type { NotificationDraft } from '../useNotificationDraft';
import { ChannelResultCard } from './ChannelResultCard';
import {
  buildEvaluateContext,
  contextSourceComplete,
  ContextSourcePicker,
  type ContextSourceValue,
} from './ContextSourcePicker';
import { buildOverrides } from './draftOverrides';

interface Props {
  open: boolean;
  onClose: () => void;
  detail: NotificationDetail;
  draft: NotificationDraft;
  /** Page-lifted sticky render context (shared with the docked live preview). */
  source: ContextSourceValue;
  onSourceChange: (v: ContextSourceValue) => void;
  /** Narrow the render to one method and preview its unsaved draft edits. */
  deliveryMethodId?: number | undefined;
}

export function TestRenderDialog({
  open,
  onClose,
  detail,
  draft,
  source,
  onSourceChange,
  deliveryMethodId,
}: Props) {
  const [debugMarkers, setDebugMarkers] = useState(false);

  const { data: events = [] } = useQuery({
    queryKey: ['notification-test-render-events'],
    queryFn: listTestRenderEvents,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const eventInfo = events.find((e) => e.code === detail.notification.event);
  const entityKind = eventInfo?.entity_kind ?? null;

  const { fieldOverrides, notificationOverrides } = useMemo(
    () => buildOverrides(draft, deliveryMethodId),
    // dirtyPaths is replaced (never mutated) on every draft edit.
    [draft, deliveryMethodId],
  );
  const hasOverrides = fieldOverrides !== null || notificationOverrides !== null;

  const targetDm =
    deliveryMethodId != null
      ? draft.draft.deliveryMethods.find((d) => d.id === deliveryMethodId)
      : undefined;

  const mutation = useMutation<TestRenderResult, unknown, void>({
    mutationFn: () =>
      testRender({
        notification: detail.id,
        ...(deliveryMethodId != null ? { delivery_method: deliveryMethodId } : {}),
        context: buildEvaluateContext(source),
        debug_markers: debugMarkers,
        ...(fieldOverrides ? { field_overrides: fieldOverrides } : {}),
        ...(notificationOverrides ? { notification_overrides: notificationOverrides } : {}),
      }),
  });

  const requestError =
    mutation.error instanceof ConsoleApiError
      ? mutation.error.message
      : mutation.error
        ? 'Test render request failed.'
        : null;

  const canRender = contextSourceComplete(source, entityKind);
  const out = mutation.data;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Test render"
      size="lg"
      actions={<Button onClick={onClose}>Close</Button>}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ValueActions chip actions={notificationActions(detail.id)}>
            <Chip size="small" variant="outlined" label={`Notification #${detail.id}`} />
          </ValueActions>
          <ValueActions
            chip
            actions={eventActions(detail.notification.event, {
              tenantId: detail.notification.tour_operator,
            })}
          >
            <Chip
              size="small"
              variant="outlined"
              label={detail.notification.event}
              sx={{ fontFamily: 'monospace' }}
            />
          </ValueActions>
          {targetDm && (
            <Chip
              size="small"
              color="primary"
              label={`${DELIVERY_METHOD_LABELS[targetDm.method] ?? targetDm.method} #${targetDm.id}`}
            />
          )}
          {hasOverrides && (
            <Chip size="small" color="warning" variant="outlined" label="Previewing unsaved draft" />
          )}
        </Stack>

        {deliveryMethodId == null && draft.dirty && (
          <Alert severity="info">
            Unsaved changes aren&apos;t reflected in multi-channel renders — save first or test one
            method.
          </Alert>
        )}

        <ContextSourcePicker
          value={source}
          onChange={onSourceChange}
          entityKind={entityKind}
          entityFamily={eventInfo?.family}
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={debugMarkers}
              onChange={(e) => setDebugMarkers(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              Block boundary markers (&lt;!-- block:… --&gt; comments around each block value)
            </Typography>
          }
        />

        <Box>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={!canRender || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Rendering…' : 'Render'}
          </Button>
        </Box>

        {requestError && <Alert severity="error">{requestError}</Alert>}

        {out && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {!out.resolved && (
                <Alert severity="warning" sx={{ py: 0 }}>
                  Some lazy context fields could not be resolved — output may be incomplete.
                </Alert>
              )}
              {out.language && <Chip size="small" label={`Language: ${out.language}`} />}
              <Chip
                size="small"
                variant="outlined"
                label={`${out.results.length} channel${out.results.length === 1 ? '' : 's'}`}
              />
            </Stack>
            {out.results.map((res) => (
              <ChannelResultCard key={`${res.delivery_method}-${res.channel}`} res={res} />
            ))}
          </Stack>
        )}
      </Stack>
    </AppDialog>
  );
}
